import { useEffect, useState } from 'react'

/**
 * Navegación por fragmento de URL.
 *
 * Al revés que la app de escritorio, que navega con estado y sin router: esto
 * es una web y las URLs son parte de lo que hace. Un jugador manda «mira,
 * quedé cuarto» con el enlace de su torneo, vuelve con el botón atrás del
 * teléfono, y agrega la página del torneo en curso a la pantalla de inicio. Con
 * la navegación en un `useState` nada de eso funcionaría.
 *
 * Va sobre el fragmento —`#/torneo/abc`— y no sobre `history.pushState` porque
 * el site se sirve como archivos estáticos: con rutas reales, entrar directo a
 * `/torneo/abc` sería un 404 salvo que el servidor reescriba todo a
 * `index.html`, y eso es una condición del despliegue que la app no controla.
 */

export type Pestana = 'resumen' | 'mesa' | 'mesas' | 'posiciones' | 'partidas'

/**
 * Las pestañas de un torneo mirado desde la vitrina. Son las del torneo propio
 * menos las dos que dicen «mi»: sin cuenta no hay mesa ni partidas propias.
 */
export type PestanaPublica = 'resumen' | 'mesas' | 'posiciones'

export type Vista =
  | { nombre: 'inicio' }
  /* Sus torneos: el primer segmento de la pestaña «Torneos». */
  | { nombre: 'torneos' }
  /* Su ficha, sus números y los torneos que terminó. Reemplaza al historial. */
  | { nombre: 'perfil' }
  | { nombre: 'torneo'; id: string; pestana: Pestana }
  /* La libreta de anotación de una mesa, a pantalla entera. */
  | { nombre: 'anotar'; id: string; mesaId: string }
  /* La libreta personal: las partidas sueltas, fuera de los torneos. */
  | { nombre: 'libreta' }
  | { nombre: 'libreta-nueva' }
  | { nombre: 'libreta-partida'; id: string }
  /* La vitrina: se ve con y sin sesión. */
  | { nombre: 'vivo' }
  | { nombre: 'proximos' }
  | { nombre: 'resultados' }
  | { nombre: 'vivo-torneo'; id: string; pestana: PestanaPublica }
  /* El formulario de ingreso, ahora que sin sesión se entra por la vitrina. */
  | { nombre: 'ingreso' }
  /* Crear o cambiar la contraseña con el enlace que llegó por correo. */
  | { nombre: 'clave'; token: string }

const PESTANAS: readonly Pestana[] = ['resumen', 'mesa', 'mesas', 'posiciones', 'partidas']
const PESTANAS_PUBLICAS: readonly PestanaPublica[] = ['resumen', 'mesas', 'posiciones']

/** Las vistas que no piden sesión. */
export function esPublica(vista: Vista): boolean {
  return (
    vista.nombre === 'vivo' ||
    vista.nombre === 'proximos' ||
    vista.nombre === 'resultados' ||
    vista.nombre === 'vivo-torneo'
  )
}

/**
 * A qué pestaña se entra cuando el enlace no dice cuál.
 *
 * Sigue siendo la mesa y no el resumen, aunque el resumen esté primero. La
 * pregunta que trae al jugador a la página es «¿dónde juego ahora?», y hacerle
 * dar un toque más para contestarla sería cambiar la web por una más ordenada y
 * menos útil. El resumen está para el que viene a mirar el torneo, que llega
 * después y con tiempo.
 */
const PESTANA_POR_DEFECTO: Pestana = 'mesa'

/**
 * El mismo torneo, abierto como jugador y no como espectador.
 *
 * Es para el que tiene sesión y juega el torneo que encontró en la vitrina: ahí
 * no tiene que mirar su propia mesa desde afuera. El resumen de la vitrina cae
 * en su mesa, por lo mismo que `PESTANA_POR_DEFECTO`.
 */
export function vistaPropia(id: string, pestana: PestanaPublica): Vista {
  return { nombre: 'torneo', id, pestana: pestana === 'resumen' ? PESTANA_POR_DEFECTO : pestana }
}

/** El fragmento que le corresponde a una vista. Es la inversa de `leer`. */
export function rutaDe(vista: Vista): string {
  switch (vista.nombre) {
    case 'inicio':
      return '#/'
    case 'torneos':
      return '#/torneos'
    case 'perfil':
      return '#/perfil'
    case 'torneo':
      return `#/torneo/${encodeURIComponent(vista.id)}/${vista.pestana}`
    case 'anotar':
      return `#/torneo/${encodeURIComponent(vista.id)}/anotar/${encodeURIComponent(vista.mesaId)}`
    case 'libreta':
      return '#/libreta'
    case 'libreta-nueva':
      return '#/libreta/nueva'
    case 'libreta-partida':
      return `#/libreta/${encodeURIComponent(vista.id)}`
    case 'vivo':
      return '#/en-vivo'
    case 'proximos':
      return '#/proximos'
    case 'resultados':
      return '#/resultados'
    case 'vivo-torneo':
      return `#/en-vivo/${encodeURIComponent(vista.id)}/${vista.pestana}`
    case 'ingreso':
      return '#/ingreso'
    case 'clave':
      return `#/clave/${encodeURIComponent(vista.token)}`
  }
}

/** Lee la vista del fragmento actual. Lo que no reconoce cae en el inicio. */
function leer(fragmento: string): Vista {
  const tramos = fragmento.replace(/^#\/?/, '').split('/').filter(Boolean)
  const primero = tramos[0]

  if (!primero) return { nombre: 'inicio' }
  if (primero === 'torneos') return { nombre: 'torneos' }
  /* El historial pasó a ser parte del perfil: los enlaces viejos siguen sirviendo. */
  if (primero === 'perfil' || primero === 'historial') return { nombre: 'perfil' }
  if (primero === 'ingreso') return { nombre: 'ingreso' }
  if (primero === 'proximos') return { nombre: 'proximos' }
  if (primero === 'resultados') return { nombre: 'resultados' }
  if (primero === 'libreta') {
    const id = tramos[1]
    if (!id) return { nombre: 'libreta' }
    if (id === 'nueva') return { nombre: 'libreta-nueva' }
    return { nombre: 'libreta-partida', id: decodeURIComponent(id) }
  }
  if (primero === 'clave') {
    const token = tramos[1]
    return token ? { nombre: 'clave', token: decodeURIComponent(token) } : { nombre: 'ingreso' }
  }

  if (primero === 'en-vivo') {
    const id = tramos[1]
    if (!id) return { nombre: 'vivo' }
    const pedida = tramos[2]
    /* Por el resumen y no por las mesas, al revés que el torneo propio: el que
       entra sin cuenta no viene a buscar su mesa, viene a ver cómo va. */
    const pestana = PESTANAS_PUBLICAS.find((p) => p === pedida) ?? 'resumen'
    return { nombre: 'vivo-torneo', id: decodeURIComponent(id), pestana }
  }

  if (primero === 'torneo') {
    const id = tramos[1]
    if (!id) return { nombre: 'torneos' }
    const pedida = tramos[2]
    const mesaId = tramos[3]
    if (pedida === 'anotar' && mesaId) {
      return { nombre: 'anotar', id: decodeURIComponent(id), mesaId: decodeURIComponent(mesaId) }
    }
    const pestana = PESTANAS.find((p) => p === pedida) ?? PESTANA_POR_DEFECTO
    return { nombre: 'torneo', id: decodeURIComponent(id), pestana }
  }

  return { nombre: 'inicio' }
}

/**
 * Si ya se navegó dentro de la web. Sin esto, `volver` no sabría si el paso
 * anterior del historial es una pantalla propia o la página de la que se vino
 * (un enlace abierto desde WhatsApp, por ejemplo).
 */
let navegoAdentro = false

/** Cambia de pantalla apilando en el historial del navegador. */
export function ir(vista: Vista): void {
  const destino = rutaDe(vista)
  if (window.location.hash === destino) return
  navegoAdentro = true
  window.location.hash = destino
}

/**
 * El botón de volver de una pantalla.
 *
 * Si se llegó navegando, es el atrás del navegador: así no queda apilada una
 * copia de la pantalla anterior. Si se entró directo por el enlace, no hay
 * atrás propio y se reemplaza por `alternativa`.
 */
export function volver(alternativa: Vista): void {
  if (navegoAdentro) window.history.back()
  else reemplazar(alternativa)
}

/**
 * Reemplaza la vista actual sin dejar rastro en el historial.
 *
 * Es para las correcciones —un torneo que no existe, una pestaña inventada—,
 * donde apilar dejaría el botón atrás rebotando entre la ruta rota y la buena.
 */
export function reemplazar(vista: Vista): void {
  window.location.replace(rutaDe(vista))
}

export function useVista(): Vista {
  const [vista, setVista] = useState<Vista>(() => leer(window.location.hash))

  useEffect(() => {
    const alCambiar = () => setVista(leer(window.location.hash))
    window.addEventListener('hashchange', alCambiar)
    return () => window.removeEventListener('hashchange', alCambiar)
  }, [])

  return vista
}
